import React, { useMemo } from "react";
import moment from "moment";
import {
  Footer,
  Logo,
  PurchaseInfo,
  PurchaseInfoGrid,
  PurchaseInfoText,
  PurchaseTable,
  PurchaseTableGrid,
  PurchaseTableText,
  Receipt,
  ReceiptDetail,
  Signature,
  TaxContainer,
  TotalContainer,
} from "./styles";
import InformaLogo from "../../../../../../newUXAssets/images/InformaLogo.png";
import { Flex } from "../../PurchaseSummary/style";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { formatCurrency } from "../../Checkout/components/PaymentMethods/utils";
import parsePhoneNumber from "libphonenumber-js";

const currentDate = new Date()
  .toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
  .replace(",", "");

export default function PurchaseReceipt({ data }) {
  return (
    <>
      <style>{`
        @media print {
          .receipt-signature-block {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>
      <Receipt>
        <ReceiptDetail position="top">
          <TopDetail />
        </ReceiptDetail>

        <Logo src={InformaLogo} alt="Informa Logo" />
        <Flex direction="column" flex justify="flex-start">
          <PurchaseInfoTable data={data} />
          <ProductTable data={data} />
          <ReceiptSignature data={data} />
          <ReceiptFooter />
        </Flex>
        <ReceiptDetail position="bottom">
          <BotttomDetail />
        </ReceiptDetail>
      </Receipt>
    </>
  );
}

function PurchaseInfoTable({ data }) {
  const fantasyname = localStorage.getItem("serviceFantasyname");
  const contractNumber =
    localStorage.getItem("serviceContractNumber") ?? data.contractNumber;
  const { user } = useSelector((s) => s.auth);
  const staffAccess = useSelector((state) => state.staff_access);
  const checkoutInfo = data?.checkoutInfo ?? staffAccess.checkoutInfo ?? {};
  const purchaseInfo = data?.purchaseInfo ?? staffAccess.purchaseInfo ?? {};
  const CNPJ = data?.cnpj ?? staffAccess.CNPJ;
  const taxClassification =
    data?.taxClassification ?? staffAccess.taxClassification ?? "-";
  const intl = useIntl();
  const purchaseDate =
    moment(data?.purchaseInfo?.purchaseDate).format("DD/MM/YYYY") ??
    currentDate;

  const phone = (() => {
    const rawPhone = purchaseInfo?.phone;
    if (!rawPhone) return null;
    try {
      return rawPhone.slice(0, 3) === "+55" && intl.locale === "pt"
        ? parsePhoneNumber(rawPhone).formatNational()
        : parsePhoneNumber(rawPhone).formatInternational();
    } catch (_) {
      return rawPhone;
    }
  })();
  return (
    <PurchaseInfo className="receipt-signature-block">
      <Flex
        direction="column"
        gap="24px"
        justify="flex-start"
        items="flex-start"
      >
        <div>
          <PurchaseInfoText type="title">
            <FormattedMessage id="buyer" />
          </PurchaseInfoText>

          {!purchaseInfo.name ? (
            "-"
          ) : (
            <>
              {purchaseInfo?.name && (
                <PurchaseInfoText type="desc">
                  {purchaseInfo.name}
                </PurchaseInfoText>
              )}
              {purchaseInfo?.document && (
                <PurchaseInfoText>{purchaseInfo.document}</PurchaseInfoText>
              )}
              {phone && <PurchaseInfoText>{phone}</PurchaseInfoText>}
            </>
          )}
        </div>

        <div>
          <PurchaseInfoText type="title">
            <FormattedMessage id="company" />
          </PurchaseInfoText>
          <PurchaseInfoText type="desc">{fantasyname}</PurchaseInfoText>
          <PurchaseInfoText>{CNPJ}</PurchaseInfoText>
        </div>

        <div>
          <PurchaseInfoText type="title">
            <FormattedMessage id="seller" />
          </PurchaseInfoText>
          <PurchaseInfoText type="desc">
            {purchaseInfo?.seller ?? user.name.split("@")[0]}
          </PurchaseInfoText>
        </div>
      </Flex>

      <Flex
        direction="column"
        gap="24px"
        justify="flex-start"
        items="flex-start"
      >
        <PurchaseInfoGrid>
          <div>
            <PurchaseInfoText type="title">
              <FormattedMessage id="contract" />
            </PurchaseInfoText>
            <PurchaseInfoText type="desc">{contractNumber}</PurchaseInfoText>
          </div>

          <div>
            <PurchaseInfoText type="title">
              <FormattedMessage id="tax_classification" />
            </PurchaseInfoText>
            <PurchaseInfoText type="desc">
              {taxClassification && taxClassification !== "-" ? (
                <FormattedMessage
                  id={String(taxClassification).toLowerCase()}
                  defaultMessage={String(taxClassification)}
                />
              ) : (
                "-"
              )}
            </PurchaseInfoText>
          </div>
          <div>
            <PurchaseInfoText type="title">
              <FormattedMessage id="purchase_order2" />
            </PurchaseInfoText>
            <PurchaseInfoText type="desc">
              {checkoutInfo.orderNumber}
            </PurchaseInfoText>
          </div>
        </PurchaseInfoGrid>

        <PurchaseInfoGrid>
          <div>
            <PurchaseInfoText type="title">
              <FormattedMessage id="purchase_date" />
            </PurchaseInfoText>
            <PurchaseInfoText type="desc">{purchaseDate}</PurchaseInfoText>
          </div>

          <div>
            <PurchaseInfoText type="title">
              <FormattedMessage id="payment_status" />
            </PurchaseInfoText>
            <PurchaseInfoText type="desc">
              <FormattedMessage id="approved" />
            </PurchaseInfoText>
          </div>

          <div>
            <PurchaseInfoText type="title">
              <FormattedMessage id="payment_method2" />
            </PurchaseInfoText>
            {!purchaseInfo.paymentMethod ? (
              "-"
            ) : (
              <>
                {purchaseInfo?.paymentMethod && (
                  <PurchaseInfoText type="desc">
                    <FormattedMessage id={purchaseInfo.paymentMethod} />
                  </PurchaseInfoText>
                )}
              </>
            )}
          </div>
        </PurchaseInfoGrid>

        {(purchaseInfo?.cardReader || purchaseInfo?.brand) && (
          <PurchaseInfoGrid>
            {purchaseInfo?.cardReader && (
              <div>
                <PurchaseInfoText type="title">
                  <FormattedMessage id="card_reader2" />
                </PurchaseInfoText>
                <PurchaseInfoText type="desc">
                  {purchaseInfo.cardReader}
                </PurchaseInfoText>
              </div>
            )}

            {purchaseInfo?.brand && (
              <div>
                <PurchaseInfoText type="title">
                  <FormattedMessage id="brand" />
                </PurchaseInfoText>
                <PurchaseInfoText type="desc">
                  {purchaseInfo.brand}
                </PurchaseInfoText>
              </div>
            )}
          </PurchaseInfoGrid>
        )}
      </Flex>
    </PurchaseInfo>
  );
}

function ProductTable({ data }) {
  const staffAccess = useSelector((state) => state.staff_access);
  const cartProducts = React.useMemo(() => {
    const base = data?.cartProducts ?? staffAccess.cartProducts ?? [];
    return base;
  }, [data?.cartProducts, staffAccess.cartProducts]);

  const intl = useIntl();
  return (
    <PurchaseTable>
      <PurchaseTableGrid>
        <Flex justify="flex-start">
          <PurchaseTableText title>
            <FormattedMessage id="product" />
          </PurchaseTableText>
        </Flex>
        <Flex height="34px">
          <PurchaseTableText title>
            <FormattedMessage id="qty2" />
          </PurchaseTableText>
        </Flex>
        <Flex height="34px">
          <PurchaseTableText title>
            <FormattedMessage id="unitary_price" />
          </PurchaseTableText>
        </Flex>
        <Flex height="34px">
          <PurchaseTableText title>
            <FormattedMessage id="total_value" />
          </PurchaseTableText>
        </Flex>
      </PurchaseTableGrid>

      {cartProducts.map((p, i) => (
        <PurchaseTableGrid
          key={p.code ?? `${i}-${p.title?.[intl.locale] ?? "item"}`}
          noBorder={cartProducts.length === i + 1}
        >
          <Flex
            paddingTop={data && "10px"}
            paddingBottom={data && "10px"}
            justify="flex-start"
          >
            <PurchaseTableText>
              {data ? p.title : p.title?.[intl.locale]}
            </PurchaseTableText>
          </Flex>
          <Flex height="34px">
            <PurchaseTableText>{p.qty ?? 1}</PurchaseTableText>
          </Flex>
          <Flex height="34px">
            <PurchaseTableText>
              {formatCurrency({
                value: p.value,
                locale: intl.locale,
                currency: p.currency,
              })}
            </PurchaseTableText>
          </Flex>
          <Flex height="34px">
            <PurchaseTableText>
              {formatCurrency({
                value: p.value * (p.qty ?? 1),
                locale: intl.locale,
                currency: p.currency,
              })}
            </PurchaseTableText>
          </Flex>
        </PurchaseTableGrid>
      ))}
    </PurchaseTable>
  );
}

function ReceiptFooter() {
  return (
    <Footer className="receipt-signature-block">
      <div>
        <p>INFORMA MARKETS LTDA.</p>
        <p>CNPJ: 01.914.765/0001-08</p>
        <p>INSCR. MUNICIPAL: 3.308.884-0</p>
        <p>AV. DOUTORA RUTH CARDOSO, 7.221 - ANDAR 22 - BIRMANN 21</p>
        <p>PINHEIROS, CEP: 04552-902 - SÃO PAULO - SP/Brasil</p>
      </div>
    </Footer>
  );
}

function ReceiptSignature({ data }) {
  const intl = useIntl();
  const staffAccess = useSelector((state) => state.staff_access);
  const checkoutInfo = data?.checkoutInfo ??
    staffAccess.checkoutInfo ?? { taxes: [] };
  const cartProducts = React.useMemo(() => {
    const base = data?.cartProducts ?? staffAccess.cartProducts ?? [];
    return base;
  }, [data?.cartProducts, staffAccess.cartProducts]);
  const currencyFromData = data?.currency ?? cartProducts?.[0]?.currency;
  const currency = currencyFromData ?? staffAccess.currency ?? "BRL";
  const dataSubtotal = data?.subtotal
    ? formatCurrency({
        value: data?.subtotal,
        locale: intl.locale,
        currency,
      })
    : undefined;

  const totals = useMemo(() => {
    const length = cartProducts.reduce((acc, curr) => acc + (curr.qty ?? 1), 0);
    const subtotal = cartProducts.reduce(
      (acc, curr) => acc + curr.value * (curr.qty ?? 1),
      0
    );

    return {
      subtotal: formatCurrency({
        value: subtotal,
        locale: intl.locale,
        currency,
      }),
      length,
    };
  }, [cartProducts, currency, intl.locale]);

  return (
    <>
      <Flex className="receipt-signature-block" gap="45px">
        <Signature>
          <Flex direction="column">
            <PurchaseInfoText type="desc" mb="0">
              <FormattedMessage id="FINANCIAL_INFORMA_SIGNATURE" />
            </PurchaseInfoText>
          </Flex>
        </Signature>

        <TaxContainer>
          <Flex justify="space-between">
            <p>
              Subtotal ({totals.length}{" "}
              {intl.formatMessage({ id: totals.length > 1 ? "items" : "item" })}
              )
            </p>
            <p>{dataSubtotal ?? totals.subtotal}</p>
          </Flex>
          {(checkoutInfo.taxes ?? []).map((t, i) => (
            <Flex justify="space-between" key={i}>
              <p>{t.label}</p>
              <p>
                {formatCurrency({
                  value: t.value,
                  locale: intl.locale,
                  currency,
                })}
              </p>
            </Flex>
          ))}
          <Flex justify="space-between">
            <p>
              <FormattedMessage id="discounts" />
            </p>
            <p>
              {formatCurrency({
                value:
                  (checkoutInfo.discounts ?? 0) > 0
                    ? (checkoutInfo.discounts ?? 0) * -1
                    : 0,
                locale: intl.locale,
                currency,
              })}
            </p>
          </Flex>
        </TaxContainer>
      </Flex>
      <TotalContainer>
        <div>
          <p>
            <FormattedMessage id="total" />
          </p>
          <p>
            {formatCurrency({
              value: checkoutInfo.totalValue ?? 0,
              locale: intl.locale,
              currency,
            })}
          </p>
        </div>
      </TotalContainer>
    </>
  );
}

const TopDetail = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="213"
    height="26"
    fill="none"
    viewBox="0 0 213 26"
  >
    <path fill="#33D27E" stroke="#33D27E" d="M0 25V0h212l-45.693 25z"></path>
  </svg>
);

const BotttomDetail = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="269"
    height="26"
    fill="none"
    viewBox="0 0 269 26"
  >
    <path fill="#33D27E" stroke="#33D27E" d="M269 26V1H68.838L1 26z"></path>
  </svg>
);
